import { useState, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@ndd/ui";
import { SourceQuoteBlock } from "@/components/narrative/source-quote-block";
import { LabeledBody } from "@/components/narrative/labeled-body";
import type { XmlItemRating } from "../../../../shared/analysis-types";
import { CopyFormattedButton } from "@/components/copy-formatted-button";
import { formatScorecardItemCopy } from "@/lib/analysis-copy-format";
import { RatingBadge } from "./rating-badge";
import { cn } from "@/lib/utils";

type NonNaRating = Exclude<XmlItemRating, "na">;

interface ScorecardItemRowProps {
  rating: NonNaRating;
  label: string;
  summary: string;
  evidence?: string;
  what_to_try?: string;
  why_it_matters?: string;
  copy_paste_reinforcement?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

const tierTint: Record<NonNaRating, string> = {
  exceeds: "bg-forest-100/50",
  great: "bg-forest-100/50",
  meets: "",
  not_yet: "bg-sunlight-100/50",
  needs_improvement: "bg-error-light/50",
  unknown: "bg-error-light/30",
};

const expandedFields: Record<
  NonNaRating,
  { evidence: boolean; whatToTry: boolean; whyItMatters: boolean; reinforcement: boolean }
> = {
  meets: { evidence: true, whatToTry: false, whyItMatters: false, reinforcement: false },
  needs_improvement: { evidence: true, whatToTry: true, whyItMatters: false, reinforcement: false },
  not_yet: { evidence: true, whatToTry: true, whyItMatters: true, reinforcement: false },
  great: { evidence: true, whatToTry: false, whyItMatters: false, reinforcement: true },
  exceeds: { evidence: true, whatToTry: false, whyItMatters: false, reinforcement: true },
  unknown: { evidence: true, whatToTry: true, whyItMatters: true, reinforcement: true },
};

export function ScorecardItemRow({
  rating,
  label,
  summary,
  evidence,
  what_to_try,
  why_it_matters,
  copy_paste_reinforcement,
  open: controlledOpen,
  onOpenChange,
  className,
}: ScorecardItemRowProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (onOpenChange) onOpenChange(value);
      if (!isControlled) setInternalOpen(value);
    },
    [isControlled, onOpenChange],
  );

  const fields = expandedFields[rating];

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange}>
      <div
        className={cn(
          "group rounded-xl border border-sand-200 transition-colors",
          open ? tierTint[rating] || "bg-card" : "bg-card",
          className,
        )}
      >
        <div className="flex w-full items-start gap-3 px-4 py-3 text-left">
          <RatingBadge rating={rating} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-base font-bold text-foreground">
                {label}
              </span>
              <CopyFormattedButton
                format={formatScorecardItemCopy({
                  rating,
                  label,
                  summary,
                  evidence,
                  what_to_try,
                  why_it_matters,
                  copy_paste_reinforcement,
                })}
                className="text-[11px] opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
              >
                Copy item
              </CopyFormattedButton>
            </div>
            {!open && (
              <p className="mt-1 text-sm text-sand-600">
                {summary}
              </p>
            )}
          </div>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sand-400 transition-colors hover:bg-sand-100 hover:text-foreground"
            >
              <ChevronDown
                className={cn(
                  "size-5 transition-transform",
                  open && "rotate-180",
                )}
              />
              <span className="sr-only">
                {open ? "Collapse item" : "Expand item"}
              </span>
            </button>
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent>
          <div className="flex flex-col gap-3 px-4 pb-4 pl-[108px]">
            <p className="text-sm text-sand-600">{summary}</p>

            {fields.evidence && evidence && (
              <SourceQuoteBlock quote={evidence} label="EVIDENCE" />
            )}

            {fields.whatToTry && what_to_try && (
              <LabeledBody label="What to try" variant="action">
                {what_to_try}
              </LabeledBody>
            )}

            {fields.whyItMatters && why_it_matters && (
              <LabeledBody label="Why it matters" variant="descriptive">
                {why_it_matters}
              </LabeledBody>
            )}

            {fields.reinforcement && copy_paste_reinforcement && (
              <LabeledBody label="Emphasize for reinforcement" variant="reinforcement">
                {copy_paste_reinforcement}
              </LabeledBody>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
